import { Component, useSyncExternalStore, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TurnstileWidget, TurnstileWidgetAppearance } from '@/components/Authentication/TurnstileWidget';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger.ts';
import { WAFInteractiveChallengeOverlay } from '@/UI/WAFInteractiveChallengeOverlay/WAFInteractiveChallengeOverlay';
import { WAFInteractiveHelp } from '@/UI/WAFInteractiveChallengeOverlay/WAFInteractiveHelp';
import { runGuarded } from '../runGuarded';
import { WAFClearanceDetail } from '../types';
import { getWAFTurnstileSitekey, WAF_CHALLENGE_ACTION, WAF_CLEARANCE_WIDGET_TEST_ID } from './consts';
import { cloudflareTurnstileClearanceProvider as clearanceProvider } from './CloudflareTurnstileClearanceProvider';

const logger = log.getLogger('WAFChallenge');

// Renders null on a child throw instead of the app ErrorBoundary's ErrorPage, which would fire a spurious
// posthog error_page_viewed plus a Sentry/RUM crash report for this invisible host. componentDidCatch
// settles the clearance as widget-render-crash so a render crash resolves at once instead of hanging the
// clearance indefinitely; the caller keys the boundary by flow, so each attempt gets a fresh instance that
// cannot latch a prior trip.
class SilentClearanceErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    // Settle before logging, and guard the log: a throw from a boundary's own componentDidCatch escapes to
    // the PARENT boundary (here the app's full-page ErrorPage — the outcome this class exists to prevent),
    // and the log path can throw (posthog / JSON.stringify in the middleware). settleWidgetFailure is
    // throw-free, so the clearance resolves even when the best-effort log is dropped. The raw Error also
    // serializes to {} in the log middleware, so pass its name/message explicitly.
    clearanceProvider.settleWidgetFailure(WAFClearanceDetail.WidgetRenderCrash);
    runGuarded(() =>
      logger.error('WAF clearance host render crash', {
        errorName: error.name,
        errorMessage: error.message,
      }),
    );
  }

  render(): ReactNode {
    return this.state.hasError ? null : this.props.children;
  }
}

export function CloudflareTurnstileClearanceHost(): ReactElement {
  const { t } = useTranslation();
  const isSolving = useSyncExternalStore(clearanceProvider.subscribe, clearanceProvider.getSnapshot);
  const isInteractive = useSyncExternalStore(clearanceProvider.subscribe, clearanceProvider.getInteractiveSnapshot);

  return (
    // The boundary wraps the whole overlay (chrome included), so a throw from the theme providers / focus trap
    // / DS components is contained too, not just a widget crash. Keyed by flow (isSolving) so each attempt gets
    // a fresh unlatched boundary while promote/demote (isInteractive) never remounts the live widget mid-flow.
    <SilentClearanceErrorBoundary key={isSolving ? 'solving' : 'idle'}>
      <WAFInteractiveChallengeOverlay
        isInteractive={isInteractive}
        title={t(I18N_KEYS.SECURITY_CHALLENGE.TITLE)}
        body={t(I18N_KEYS.SECURITY_CHALLENGE.BODY)}
        help={<WAFInteractiveHelp />}
      >
        {isSolving && (
          /* Only terminal outcomes settle the clearance. onError is record-only (never settle) so Cloudflare's
              retry:'auto' still runs — it just names a non-retryable failure (e.g. an unauthorized sitekey)
              behind a slow clearance. onTimeout/onExpire stay unwired: the widget's refresh-*:'auto' handles
              those, and a solve that never resolves keeps running behind the slow notice until the user reloads. */
          <TurnstileWidget
            sitekey={getWAFTurnstileSitekey()}
            action={WAF_CHALLENGE_ACTION}
            appearance={TurnstileWidgetAppearance.InteractionOnly}
            data-testid={WAF_CLEARANCE_WIDGET_TEST_ID}
            onSuccess={(_token, clearanceCookieIssued) => clearanceProvider.settleWidgetSuccess(clearanceCookieIssued)}
            onError={(errorCode) => clearanceProvider.recordWidgetError(errorCode)}
            onUnsupported={() => clearanceProvider.settleWidgetFailure(WAFClearanceDetail.WidgetUnsupportedInBrowser)}
            onUnavailable={() => clearanceProvider.settleWidgetFailure(WAFClearanceDetail.WidgetUnavailable)}
            onBeforeInteractive={() => clearanceProvider.enterInteractive()}
            onAfterInteractive={() => clearanceProvider.exitInteractive()}
            onScriptLoaded={(wasScriptAlreadyLoaded) => clearanceProvider.markScriptLoaded(wasScriptAlreadyLoaded)}
            onRendered={(readToken) => clearanceProvider.markWidgetRendered(readToken)}
          />
        )}
      </WAFInteractiveChallengeOverlay>
    </SilentClearanceErrorBoundary>
  );
}
